import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useServerApi } from "../hooks/userServerApi";
import { useTabStateOptional } from "../context/tabContext";
import { normalizeSourceOfFundOptions } from "../helpers/riskControlSettingsUtils";

const DEFAULT_SETTINGS = {
  sourceOfFundOptions: normalizeSourceOfFundOptions(),
};

const RiskControlSettingsContext = createContext(DEFAULT_SETTINGS);
const settingsListeners = new Set();

export const invalidateRiskControlSettings = () => {
  settingsListeners.forEach((listener) => listener());
};

export const RiskControlSettingsProvider = ({ children }) => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const tabState = useTabStateOptional();
  const location = useLocation();
  const activePath = tabState?.activeTab?.pathname ?? "";
  const tabIsActive = !activePath || activePath === location.pathname;

  const mountedRef = useRef(true);
  const loadSettings = useCallback(async () => {
    try {
      const result = await apiRef.current.search({
        apiEntity: "setting",
        pageSize: 1,
      });
      if (!mountedRef.current) return;
      setSettings({
        sourceOfFundOptions: normalizeSourceOfFundOptions(
          result?.docs?.[0]?.riskControl?.sourceOfFundOptions
        ),
      });
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadSettings();
    settingsListeners.add(loadSettings);
    return () => {
      mountedRef.current = false;
      settingsListeners.delete(loadSettings);
    };
  }, [loadSettings]);

  useEffect(() => {
    if (tabIsActive) loadSettings();
  }, [tabIsActive, loadSettings]);

  const value = useMemo(
    () => settings,
    [settings.sourceOfFundOptions]
  );

  return (
    <RiskControlSettingsContext.Provider value={value}>
      {children}
    </RiskControlSettingsContext.Provider>
  );
};

export const useRiskControlSettings = () => useContext(RiskControlSettingsContext);
